import { createAsyncThunk } from "@reduxjs/toolkit";
import { api } from "../../api_middleware";
import { Asset } from "../reducers/assetReducer";
import {
  AssetHealthInfo,
  NamespaceHealthSummary,
  MemoryIntegrityCheck,
  RebuildResponse,
} from "../../types/memoryHealth";

export const fetchAssets = createAsyncThunk("assets/fetchAssets", async () => {
  const response = await api("v1/admin/assets/", "GET");
  return response;
});

export const fetchAssetsByNamespace = createAsyncThunk(
  "assets/fetchAssetsByNamespace",
  async (namespace: string) => {
    const response = await api(`v1/admin/assets/?namespace=${namespace}`, "GET");
    return response;
  }
);

interface FetchAssetParams {
  namespace: string;
  assetId: string;
}
export const fetchAsset = createAsyncThunk(
  "assets/fetchAsset",
  async (params: FetchAssetParams) => {
    const { namespace, assetId } = params;
    const response = await api(
      `v1/admin/assets/${namespace}/${assetId}`,
      "GET"
    );
    return response;
  }
);

interface AssetContentItem {
  text: string;
  image_url?: string;
}
interface AssetContentRequest {
  type: string; // e.g. "story/faq_entry"
  subject?: string;
  story_items?: AssetContentItem[];
  faq_entry?: string;
  faq_topic?: string;
  faq_media?: Record<string, any>;
}
interface CreateAssetParams {
  namespace: string;
  id: string;
  type: string;
  contents: AssetContentRequest[];
}
export const createAsset = createAsyncThunk(
  "assets/createAsset",
  async (params: CreateAssetParams) => {
    const response = await api("v1/admin/assets/", "POST", params);
    return response;
  }
);

export const updateAsset = createAsyncThunk(
  "assets/updateAsset",
  async (asset: Asset) => {
    const response = await api(`v1/admin/assets/`, "POST", asset);
    return response;
  }
);

interface DeleteAssetParams {
  namespace: string;
  assetId: string;
}
export const deleteAsset = createAsyncThunk(
  "assets/deleteAsset",
  async (params: DeleteAssetParams) => {
    const { namespace, assetId } = params;
    const response = await api(
      `v1/admin/assets/${namespace}/${assetId}`,
      "DELETE"
    );
    return response;
  }
);

interface PublishAssetParams {
  namespace: string;
  assetId: string;
}
export const publishAsset = createAsyncThunk(
  "assets/publishAsset",
  async (params: PublishAssetParams) => {
    const { namespace, assetId } = params;
    const response = await api(
      `v1/admin/assets/${namespace}/${assetId}/publish`,
      "POST"
    );
    return response;
  }
);

interface MakeStableAssetParams {
  namespace: string;
  assetId: string;
}
export const makeStableAsset = createAsyncThunk(
  "assets/makeStableAsset",
  async (params: MakeStableAssetParams) => {
    const { namespace, assetId } = params;
    const response = await api(
      `v1/admin/assets/${namespace}/${assetId}/make_stable`,
      "POST"
    );
    return response;
  }
);

interface UploadPublicAssetParams {
  folder: string;
  file: File;
}
export const uploadPublicAsset = createAsyncThunk(
  "assets/uploadPublicAsset",
  async (params: UploadPublicAssetParams) => {
    const formData = new FormData();
    formData.append("folder", params.folder);
    formData.append("file", params.file);

    const response = await api(
      "v1/admin/upload_public_asset",
      "POST",
      formData
    );
    return response as { url: string };
  }
);

// ============= Memory Health Actions =============

interface FetchAllNamespacesHealthParams {
  groupPairs?: boolean;
  onlyWithIssues?: boolean;
}

// Fetch all namespaces health summaries with filtering
export const fetchAllNamespacesHealth = createAsyncThunk(
  "assets/fetchAllNamespacesHealth",
  async (params?: FetchAllNamespacesHealthParams) => {
    const queryParams = new URLSearchParams();
    if (params?.groupPairs) queryParams.append('group_pairs', 'true');
    if (params?.onlyWithIssues) queryParams.append('only_with_issues', 'true');
    
    const queryString = queryParams.toString();
    const url = queryString ? `v1/admin/assets-health-all?${queryString}` : `v1/admin/assets-health-all`;
    
    const response = await api(url, "GET");
    return response;
  }
);

interface FetchNamespaceHealthParams {
  namespace: string;
  includeDeleted?: boolean;
  includeDrafts?: boolean;
  onlyActionable?: boolean;
}

// Fetch namespace health summary with filtering
export const fetchNamespaceHealth = createAsyncThunk<
  NamespaceHealthSummary,
  string | FetchNamespaceHealthParams
>("assets/fetchNamespaceHealth", async (params: string | FetchNamespaceHealthParams) => {
  // Handle both string (backward compatibility) and params object
  if (typeof params === 'string') {
    // Simple string namespace - no filtering
    const response = await api(`v1/admin/assets-health/${params}`, "GET");
    return response as NamespaceHealthSummary;
  }
  
  // Object with filtering options
  const { namespace, includeDeleted, includeDrafts, onlyActionable } = params;
  const queryParams = new URLSearchParams();
  if (includeDeleted) queryParams.append('include_deleted', 'true');
  if (includeDrafts) queryParams.append('include_drafts', 'true');
  if (onlyActionable) queryParams.append('only_actionable', 'true');
  
  const queryString = queryParams.toString();
  const url = queryString ? `v1/admin/assets-health/${namespace}?${queryString}` : `v1/admin/assets-health/${namespace}`;
  
  const response = await api(url, "GET");
  return response as NamespaceHealthSummary;
});

// Fetch single asset health
export const fetchAssetHealth = createAsyncThunk<
  AssetHealthInfo,
  { namespace: string; assetId: string }
>(
  "assets/fetchAssetHealth",
  async ({ namespace, assetId }: { namespace: string; assetId: string }) => {
    const response = await api(
      `v1/admin/assets-health/${namespace}/${assetId}`,
      "GET"
    );
    return response as AssetHealthInfo;
  }
);

// Check memory integrity
export const checkMemoryIntegrity = createAsyncThunk<
  MemoryIntegrityCheck,
  string
>("assets/checkMemoryIntegrity", async (namespace: string) => {
  const response = await api(`v1/admin/assets-health-integrity/${namespace}`, "GET");
  return response as MemoryIntegrityCheck;
});

// Rebuild memory for namespace or specific assets
interface RebuildMemoryParams {
  namespace: string;
  assetIds?: string[];
  rebuildAll?: boolean;
}
export const rebuildMemory = createAsyncThunk<
  RebuildResponse,
  RebuildMemoryParams
>("assets/rebuildMemory", async (params: RebuildMemoryParams) => {
  const response = await api(
    `v1/admin/assets-health/${params.namespace}/rebuild`,
    "POST",
    {
      asset_ids: params.assetIds,
      rebuild_all: params.rebuildAll,
    }
  );
  return response as RebuildResponse;
});
